import type { AuthPhase, AuthUser, BillingStatus } from '../context/AuthContext';
import {
  hasCreditOnlyAccess,
  hasEffectiveBillingAccess,
  isInDunningGrace,
} from '../utils/billingAccess';
import {
  canAccessIndividualPaywall,
  isSchoolAdmin,
  isSchoolManagedUser,
  isSuperAdmin,
  requiresIntakeCheck,
} from '../utils/roleUtils';
import { pathForPhase } from './phaseRedirect';

const AUXILIARY_GATED_ROUTES: Partial<Record<AuthPhase, ReadonlySet<string>>> = {
  // Plan selection doubles as an authenticated account-management surface.
  authenticated: new Set(['plan-selection']),
};

function leafForPath(path: string): string | undefined {
  return path.split('/').filter(Boolean).at(-1);
}

/**
 * Plan selection is also an authenticated account-management destination.
 * A user can retain app access through purchased credits while their recurring
 * subscription is terminal, so forcing the entire auth phase back to
 * `subscription` would incorrectly lock them out of the rest of the app.
 */
export function canRenderGatedRoute(
  phase: AuthPhase,
  routeLeaf: string | undefined,
): boolean {
  if (!routeLeaf || phase === 'loading') return false;

  const phaseLeaf = leafForPath(pathForPhase(phase));
  return routeLeaf === phaseLeaf
    || Boolean(AUXILIARY_GATED_ROUTES[phase]?.has(routeLeaf));
}

/**
 * Single successful-status phase calculation shared by boot routing and
 * post-checkout routing. Keeping this here prevents completion handlers from
 * guessing a destination from stale React state.
 */
export function phaseForAccessState(
  user: AuthUser | null,
  status: BillingStatus,
): AuthPhase {
  if (!user) return 'unauthenticated';

  const roleUser = user as any;
  const superAdmin = isSuperAdmin(roleUser);
  const schoolAdmin = isSchoolAdmin(roleUser);
  const schoolManaged = isSchoolManagedUser(roleUser);
  const hasSchool = Boolean(user.school_id);
  const effectiveBillingActive = hasEffectiveBillingAccess(status, {
    requireBoundedB2CTrial: canAccessIndividualPaywall(roleUser),
  });

  if (schoolAdmin && hasSchool && !effectiveBillingActive) {
    return 'b2b_plan_selection';
  }
  if (!status.terms_accepted) return 'terms_acceptance';
  if (!status.onboarding_completed) return 'onboarding';

  const normalizedStatus = (status.subscription_status || '').toLowerCase();
  const paymentRecoveryRequired = [
    'past_due',
    'unpaid',
    'incomplete',
    'incomplete_expired',
    'paused',
  ].includes(normalizedStatus);
  // D10: a past_due subscription keeps access while Stripe retries the charge.
  // Only the server knows whether the grace applies, so without its
  // entitlement payload past_due still goes to billing recovery.
  const inDunningGrace = isInDunningGrace(status);

  if (paymentRecoveryRequired && !inDunningGrace && !superAdmin && canAccessIndividualPaywall(roleUser)) {
    return 'billing_recovery';
  }
  if (requiresIntakeCheck(roleUser) && !status.intake_completed) {
    return 'intake';
  }

  // A finished course hands over to the paywall even though the course credit
  // grant still counts as a purchase. (Since BILL-1 the server keeps the D3
  // set — analyses, own reports, buying credits — open for these accounts;
  // routing them into the app instead is a product decision, not made yet.)
  const courseAccessExpired = Boolean(status.course_access_until) && !status.course_access_active;
  let paywalled: boolean;
  if (status.entitlements) {
    // D3: the server decides. Credit-only users (subscription lapsed, even
    // after an expired free trial) land in the app with the D3 feature set;
    // chat, ICF progress and mentor surfaces lock in place.
    paywalled = courseAccessExpired || !hasCreditOnlyAccess(status);
  } else {
    // Older backend without the payload: previous client-side rule.
    const trialActive = Boolean(status.free_trial_active);
    const trialExpired = Boolean(status.free_trial_expired);
    const hasPurchasedCredits = Boolean(status.has_purchased_credits);
    paywalled = trialExpired || courseAccessExpired || (!trialActive && !hasPurchasedCredits);
  }
  if (!effectiveBillingActive && !superAdmin && paywalled) {
    if (canAccessIndividualPaywall(roleUser)) return 'subscription';
    if (schoolManaged) return 'school_inactive';
    return 'subscription';
  }

  return 'authenticated';
}
